"use client";

import { c as _c } from "react/compiler-runtime";
import { Input } from "@components/base/input/input";
import { Select } from "@components/base/select/select";
import { COIN_PAIRS } from "@pools/consts/coins";
import type { CoinPair } from "@pools/types";
import { ChevronDown, Edit03, SearchLg, Settings02, X, XClose } from "@untitledui/icons";
import { type Key, type KeyboardEvent, type MouseEvent, memo, useEffect, useMemo, useRef, useState } from "react";
import { Button as AriaButton, Dialog as AriaDialog, DialogTrigger as AriaDialogTrigger, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Label } from "@/components/base/input/label";
import { formatFeeTierPercent } from "@/lib/pools/format-fee-tier";
import { useDebugSettings } from "@/providers/debug-settings";
import { cx } from "@/utils/cx";
import { getUniqueFeeTiers } from "../../data";
import { usePoolsFilters } from "../../hooks";
import { type PoolFilters, defaultFilters } from "../../store/poolsStore";
import { countActivePoolFilters } from "../../utils/network-filter";
import { getProtocolBadgeColor } from "../../utils/protocolBadge";
import { INPUT_FILTERS, OTHER_FEE_TIER_FILTER_ID, SELECT_FILTERS } from "./consts";
import { NetworkMultiSelect } from "./NetworkMultiSelect";
import PoolsPairs from "./PoolsPairs";
const SHOW_CUSTOM_FEE_TIER_OPTION = false;
type SetFiltersOptions = {
  immediate?: boolean;
};
type SetFiltersFn = (filters: PoolFilters, options?: SetFiltersOptions) => void;
const FILTER_CONTROL_SIZE = "md";
const FILTER_CONTROL_TEXT_CLASS_NAME = "text-sm";
const FilterOptionLabel = t0 => {
  const $ = _c(5);
  const {
    filterKey,
    item
  } = t0;
  if (item.id === "all") {
    return item.label;
  }
  if (filterKey === "protocol") {
    let t1;
    if ($[0] !== item.id) {
      t1 = getProtocolBadgeColor(item.id);
      $[0] = item.id;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== item.label || $[3] !== t1) {
      t2 = <Badge type="color" size="sm" color={t1}>{item.label}</Badge>;
      $[2] = item.label;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  return item.label;
};
const NativeSelectFilter = t0 => {
  const $ = _c(17);
  const {
    label,
    tooltip,
    items,
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] !== label || $[1] !== tooltip) {
    t1 = <Label tooltip={tooltip}>{label}</Label>;
    $[0] = label;
    $[1] = tooltip;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onChange) {
    t2 = event => onChange(event.target.value);
    $[3] = onChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== items) {
    t3 = items.map(_temp);
    $[5] = items;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== value) {
    t4 = <select value={value} onChange={t2} className="h-11 w-full appearance-none rounded-lg bg-primary py-2.5 pr-10 pl-3.5 text-left text-sm text-primary shadow-xs ring-1 ring-primary outline-focus-ring transition duration-100 ease-linear ring-inset focus:ring-2 focus:ring-brand">{t3}</select>;
    $[7] = t2;
    $[8] = t3;
    $[9] = value;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronDown aria-hidden="true" className="pointer-events-none absolute top-1/2 right-3.5 size-5 -translate-y-1/2 text-fg-quaternary" />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4) {
    t6 = <div className="relative">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t1 || $[15] !== t6) {
    t7 = <div className="flex flex-col gap-1.5">{t1}{t6}</div>;
    $[14] = t1;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
const getActiveFilterCount = (filters: PoolFilters, selectedCoinPair: CoinPair | null) => countActivePoolFilters(filters, selectedCoinPair !== null);
function displayFilters(filters: PoolFilters, volTvlEnabled: boolean): PoolFilters {
  return volTvlEnabled || filters.volTvl === "all" ? filters : {
    ...filters,
    volTvl: "all"
  };
}
const FeeTierFilter = t0 => {
  const $ = _c(54);
  const {
    filters,
    setFilters
  } = t0;
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const inputRef = useRef(null);
  let t1;
  if ($[0] !== filters.feeTier) {
    const feeTiers = getUniqueFeeTiers();
    t1 = filters.feeTier !== "all" && filters.feeTier !== OTHER_FEE_TIER_FILTER_ID && !feeTiers.includes(filters.feeTier);
    $[0] = filters.feeTier;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isCurrentValueCustom = t1;
  let t2;
  let t3;
  if ($[2] !== isInputVisible) {
    t2 = () => {
      if (isInputVisible && inputRef.current) {
        inputRef.current.focus();
      }
    };
    t3 = [isInputVisible];
    $[2] = isInputVisible;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsInputVisible(false);
      setCustomValue("");
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const dismissInput = t4;
  let t5;
  if ($[6] !== filters || $[7] !== setFilters) {
    t5 = () => {
      dismissInput();
      setFilters({
        ...filters,
        feeTier: "all"
      });
    };
    $[6] = filters;
    $[7] = setFilters;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const resetToAll = t5;
  let t6;
  if ($[9] !== filters.feeTier || $[10] !== isCurrentValueCustom) {
    t6 = () => {
      setIsDropdownOpen(false);
      setIsInputVisible(true);
      setCustomValue(isCurrentValueCustom ? filters.feeTier.replace("%", "") : "");
    };
    $[9] = filters.feeTier;
    $[10] = isCurrentValueCustom;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const openCustomInput = t6;
  let t7;
  if ($[12] !== customValue || $[13] !== filters || $[14] !== setFilters) {
    t7 = () => {
      const numValue = parseFloat(customValue);
      if (!isNaN(numValue) && numValue > 0 && numValue <= 100) {
        const formattedValue = formatFeeTierPercent(numValue);
        setFilters({
          ...filters,
          feeTier: formattedValue
        });
        dismissInput();
        return true;
      }
      return false;
    };
    $[12] = customValue;
    $[13] = filters;
    $[14] = setFilters;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const applyCustomValue = t7;
  let t8;
  if ($[16] !== filters || $[17] !== openCustomInput || $[18] !== setFilters) {
    t8 = key => {
      const keyStr = key?.toString() ?? null;
      if (keyStr === "custom") {
        openCustomInput();
      } else {
        dismissInput();
        setFilters({
          ...filters,
          feeTier: keyStr || "all"
        });
      }
    };
    $[16] = filters;
    $[17] = openCustomInput;
    $[18] = setFilters;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleSelectChange = t8;
  let t9;
  if ($[20] !== applyCustomValue || $[21] !== isCurrentValueCustom || $[22] !== resetToAll) {
    t9 = e => {
      if (e.key === "Enter") {
        e.preventDefault();
        applyCustomValue();
      } else {
        if (e.key === "Escape") {
          if (isCurrentValueCustom) {
            dismissInput();
          } else {
            resetToAll();
          }
        }
      }
    };
    $[20] = applyCustomValue;
    $[21] = isCurrentValueCustom;
    $[22] = resetToAll;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleCustomKeyDown = t9;
  let t10;
  if ($[24] !== applyCustomValue || $[25] !== customValue || $[26] !== isCurrentValueCustom || $[27] !== resetToAll) {
    t10 = () => {
      if (!customValue.trim()) {
        if (isCurrentValueCustom) {
          dismissInput();
        } else {
          resetToAll();
        }
      } else {
        const applied = applyCustomValue();
        if (!applied) {
          if (isCurrentValueCustom) {
            dismissInput();
          } else {
            resetToAll();
          }
        }
      }
    };
    $[24] = applyCustomValue;
    $[25] = customValue;
    $[26] = isCurrentValueCustom;
    $[27] = resetToAll;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const handleBlur = t10;
  let t11;
  if ($[29] !== openCustomInput) {
    t11 = e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      openCustomInput();
    };
    $[29] = openCustomInput;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const handleEditClick = t11;
  let t12;
  if ($[31] !== resetToAll) {
    t12 = e_1 => {
      e_1.preventDefault();
      e_1.stopPropagation();
      setIsDropdownOpen(false);
      resetToAll();
    };
    $[31] = resetToAll;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const handleClearClick = t12;
  const {
    label,
    items: baseItems,
    tooltip
  } = SELECT_FILTERS.feeTier;
  const customLabel = isCurrentValueCustom ? `Custom: ${filters.feeTier}` : "Custom";
  const shouldIncludeCustomOption = SHOW_CUSTOM_FEE_TIER_OPTION || isCurrentValueCustom;
  let t13;
  if ($[33] !== customLabel || $[34] !== shouldIncludeCustomOption) {
    t13 = shouldIncludeCustomOption ? [...baseItems, {
      id: "custom",
      label: customLabel
    }] : baseItems;
    $[33] = customLabel;
    $[34] = shouldIncludeCustomOption;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const items = t13;
  const displayValue = isCurrentValueCustom ? "custom" : filters.feeTier || "all";
  let t14;
  if ($[36] !== handleClearClick || $[37] !== handleEditClick || $[38] !== isCurrentValueCustom) {
    t14 = item => <Select.Item labelTextClassName={cx("w-full", FILTER_CONTROL_TEXT_CLASS_NAME)} id={item.id} textValue={item.label} value={{
      id: item.id,
      label: item.label
    }}><span className="flex w-full items-center justify-between"><span>{item.label}</span>{item.id === "custom" && isCurrentValueCustom && <span className="mr-4 flex items-center gap-3"><button type="button" onClick={handleEditClick} className="rounded-full bg-border-secondary p-1.5 text-utility-blue-500 hover:cursor-pointer hover:text-utility-blue-700" title="Edit Custom Fee"><Edit03 className="h-3.5 w-3.5" /></button><button type="button" onClick={handleClearClick} className="rounded-full bg-border-secondary p-1.5 text-utility-error-700 hover:cursor-pointer hover:bg-border-secondary hover:text-utility-error-500" title="Clear Custom Fee"><XClose className="h-3.5 w-3.5" /></button></span>}</span></Select.Item>;
    $[36] = handleClearClick;
    $[37] = handleEditClick;
    $[38] = isCurrentValueCustom;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== displayValue || $[41] !== handleSelectChange || $[42] !== isDropdownOpen || $[43] !== items || $[44] !== t14) {
    t15 = <Select size={FILTER_CONTROL_SIZE} label={label} tooltip={tooltip} items={items} value={displayValue} valueTextClassName={FILTER_CONTROL_TEXT_CLASS_NAME} onChange={handleSelectChange} isOpen={isDropdownOpen} onOpenChange={setIsDropdownOpen}>{t14}</Select>;
    $[40] = displayValue;
    $[41] = handleSelectChange;
    $[42] = isDropdownOpen;
    $[43] = items;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== customValue || $[47] !== handleBlur || $[48] !== handleCustomKeyDown || $[49] !== isInputVisible) {
    t16 = isInputVisible && <div className="absolute top-full right-0 left-0 z-10 mt-1 rounded-lg border border-border-secondary bg-bg-primary p-3 shadow-lg"><div className="flex items-center gap-2"><input ref={inputRef} type="number" step="0.01" min="0.01" max="100" placeholder="Enter Fee %" value={customValue} onChange={e_2 => setCustomValue(e_2.target.value)} onKeyDown={handleCustomKeyDown} onBlur={handleBlur} className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-brand focus:ring-1 focus:ring-border-brand focus:outline-none" /><span className="text-sm text-text-secondary">%</span></div><p className="mt-1.5 text-xs text-text-tertiary">Press Enter to apply, Esc to cancel</p></div>;
    $[46] = customValue;
    $[47] = handleBlur;
    $[48] = handleCustomKeyDown;
    $[49] = isInputVisible;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t15 || $[52] !== t16) {
    t17 = <div className="relative">{t15}{t16}</div>;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  return t17;
};
const MobileFeeTierFilter = t0 => {
  const $ = _c(41);
  const {
    filters,
    setFilters
  } = t0;
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const inputRef = useRef(null);
  let t1;
  if ($[0] !== filters.feeTier) {
    const feeTiers = getUniqueFeeTiers();
    t1 = filters.feeTier !== "all" && filters.feeTier !== OTHER_FEE_TIER_FILTER_ID && !feeTiers.includes(filters.feeTier);
    $[0] = filters.feeTier;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isCurrentValueCustom = t1;
  let t2;
  let t3;
  if ($[2] !== isInputVisible) {
    t2 = () => {
      if (isInputVisible && inputRef.current) {
        inputRef.current.focus();
      }
    };
    t3 = [isInputVisible];
    $[2] = isInputVisible;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsInputVisible(false);
      setCustomValue("");
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const dismissInput = t4;
  let t5;
  if ($[6] !== filters || $[7] !== setFilters) {
    t5 = () => {
      dismissInput();
      setFilters({
        ...filters,
        feeTier: "all"
      });
    };
    $[6] = filters;
    $[7] = setFilters;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const resetToAll = t5;
  let t6;
  if ($[9] !== customValue || $[10] !== filters || $[11] !== setFilters) {
    t6 = () => {
      const numValue = parseFloat(customValue);
      if (!isNaN(numValue) && numValue > 0 && numValue <= 100) {
        setFilters({
          ...filters,
          feeTier: formatFeeTierPercent(numValue)
        });
        dismissInput();
        return true;
      }
      return false;
    };
    $[9] = customValue;
    $[10] = filters;
    $[11] = setFilters;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const applyCustomValue = t6;
  let t7;
  if ($[13] !== applyCustomValue || $[14] !== isCurrentValueCustom || $[15] !== resetToAll) {
    t7 = e => {
      if (e.key === "Enter") {
        e.preventDefault();
        applyCustomValue();
      } else {
        if (e.key === "Escape") {
          if (isCurrentValueCustom) {
            dismissInput();
          } else {
            resetToAll();
          }
        }
      }
    };
    $[13] = applyCustomValue;
    $[14] = isCurrentValueCustom;
    $[15] = resetToAll;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleCustomKeyDown = t7;
  let t8;
  if ($[17] !== applyCustomValue || $[18] !== customValue || $[19] !== isCurrentValueCustom || $[20] !== resetToAll) {
    t8 = () => {
      if (!customValue.trim()) {
        if (isCurrentValueCustom) {
          dismissInput();
        } else {
          resetToAll();
        }
        return;
      }
      const applied = applyCustomValue();
      if (!applied) {
        if (isCurrentValueCustom) {
          dismissInput();
        } else {
          resetToAll();
        }
      }
    };
    $[17] = applyCustomValue;
    $[18] = customValue;
    $[19] = isCurrentValueCustom;
    $[20] = resetToAll;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleBlur = t8;
  const {
    label,
    items: baseItems,
    tooltip
  } = SELECT_FILTERS.feeTier;
  const customLabel = isCurrentValueCustom ? `Custom: ${filters.feeTier}` : "Custom";
  const shouldIncludeCustomOption = SHOW_CUSTOM_FEE_TIER_OPTION || isCurrentValueCustom;
  let t9;
  if ($[22] !== customLabel || $[23] !== shouldIncludeCustomOption) {
    t9 = shouldIncludeCustomOption ? [...baseItems, {
      id: "custom",
      label: customLabel
    }] : baseItems;
    $[22] = customLabel;
    $[23] = shouldIncludeCustomOption;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const items = t9;
  const displayValue = isCurrentValueCustom ? "custom" : filters.feeTier || "all";
  let t10;
  if ($[25] !== filters || $[26] !== isCurrentValueCustom || $[27] !== setFilters) {
    t10 = value => {
      if (value === "custom") {
        setIsInputVisible(true);
        setCustomValue(isCurrentValueCustom ? filters.feeTier.replace("%", "") : "");
        return;
      }
      dismissInput();
      setFilters({
        ...filters,
        feeTier: value || "all"
      });
    };
    $[25] = filters;
    $[26] = isCurrentValueCustom;
    $[27] = setFilters;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== displayValue || $[30] !== items || $[31] !== t10) {
    t11 = <NativeSelectFilter label={label} tooltip={tooltip} items={items} value={displayValue} onChange={t10} />;
    $[29] = displayValue;
    $[30] = items;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== customValue || $[34] !== handleBlur || $[35] !== handleCustomKeyDown || $[36] !== isInputVisible) {
    t12 = isInputVisible && <div className="mt-2 rounded-lg border border-border-secondary bg-bg-primary p-3 shadow-lg"><div className="flex items-center gap-2"><input ref={inputRef} type="number" step="0.01" min="0.01" max="100" placeholder="Enter Fee %" value={customValue} onChange={e_0 => setCustomValue(e_0.target.value)} onKeyDown={handleCustomKeyDown} onBlur={handleBlur} className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-brand focus:ring-1 focus:ring-border-brand focus:outline-none" /><span className="text-sm text-text-secondary">%</span></div><p className="mt-1.5 text-xs text-text-tertiary">Press Enter to apply, Esc to cancel</p></div>;
    $[33] = customValue;
    $[34] = handleBlur;
    $[35] = handleCustomKeyDown;
    $[36] = isInputVisible;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t11 || $[39] !== t12) {
    t13 = <div className="relative">{t11}{t12}</div>;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  return t13;
};
const DropdownFilters = t0 => {
  const $ = _c(7);
  const {
    filters,
    setFilters,
    exclude
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Object.entries(SELECT_FILTERS);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== exclude) {
    t2 = t1.filter(t3 => {
      const [key] = t3;
      return !exclude?.includes(SELECT_FILTERS[key as keyof typeof SELECT_FILTERS].label);
    });
    $[1] = exclude;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const filterEntries = t2;
  let t3;
  if ($[3] !== filterEntries || $[4] !== filters || $[5] !== setFilters) {
    t3 = <>{filterEntries.map(t4 => {
        const [key_0, t5] = t4;
        const {
          label,
          items,
          tooltip
        } = t5;
        if (key_0 === "network") {
          return <NetworkMultiSelect key={label} value={filters.network} onChange={network => {
            setFilters({
              ...filters,
              network
            });
          }} />;
        }
        if (key_0 === "feeTier") {
          return <FeeTierFilter key={label} filters={filters} setFilters={setFilters} />;
        }
        const filterKey = key_0 as Exclude<keyof PoolFilters, "network" | "keyword">;
        return <Select size={FILTER_CONTROL_SIZE} key={label} label={label} tooltip={tooltip} items={items} value={filters[filterKey] || "all"} valueTextClassName={FILTER_CONTROL_TEXT_CLASS_NAME} onChange={next => {
          setFilters({
            ...filters,
            [filterKey]: next as string || "all"
          });
        }}>{item => <Select.Item id={item.id} textValue={item.label} labelTextClassName={FILTER_CONTROL_TEXT_CLASS_NAME} value={{
            id: item.id,
            label: item.label
          }}><FilterOptionLabel filterKey={filterKey} item={item} /></Select.Item>}</Select>;
      })}</>;
    $[3] = filterEntries;
    $[4] = filters;
    $[5] = setFilters;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const MobileDropdownFilters = t0 => {
  const $ = _c(7);
  const {
    filters,
    setFilters,
    exclude
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Object.entries(SELECT_FILTERS);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== exclude) {
    t2 = t1.filter(t3 => {
      const [key] = t3;
      return !exclude?.includes(SELECT_FILTERS[key as keyof typeof SELECT_FILTERS].label);
    });
    $[1] = exclude;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const filterEntries = t2;
  let t3;
  if ($[3] !== filterEntries || $[4] !== filters || $[5] !== setFilters) {
    t3 = <>{filterEntries.map(t4 => {
        const [key_0, t5] = t4;
        const {
          label,
          items,
          tooltip
        } = t5;
        if (key_0 === "feeTier") {
          return <MobileFeeTierFilter key={label} filters={filters} setFilters={setFilters} />;
        }
        if (key_0 === "network") {
          return <NetworkMultiSelect key={label} variant="disclosure" value={filters.network} onChange={network => {
            setFilters({
              ...filters,
              network
            });
          }} />;
        }
        const filterKey = key_0 as Exclude<keyof PoolFilters, "network" | "keyword">;
        return <NativeSelectFilter key={label} label={label} tooltip={tooltip} items={items} value={filters[filterKey] || "all"} onChange={value => {
          setFilters({
            ...filters,
            [filterKey]: value || "all"
          });
        }} />;
      })}</>;
    $[3] = filterEntries;
    $[4] = filters;
    $[5] = setFilters;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const KeywordFilter = t0 => {
  const $ = _c(9);
  const {
    filters,
    setFilters
  } = t0;
  const [localKeyword, setLocalKeyword] = useState(filters.keyword);
  let t1;
  let t2;
  if ($[0] !== filters.keyword) {
    t1 = () => {
      setLocalKeyword(filters.keyword);
    };
    t2 = [filters.keyword];
    $[0] = filters.keyword;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== filters || $[4] !== setFilters) {
    t3 = value => {
      setLocalKeyword(value || "");
      setFilters({
        ...filters,
        keyword: value || ""
      });
    };
    $[3] = filters;
    $[4] = setFilters;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleChange = t3;
  let t4;
  if ($[6] !== handleChange || $[7] !== localKeyword) {
    t4 = <Input icon={SearchLg} size={FILTER_CONTROL_SIZE} label={INPUT_FILTERS.keyword.label} tooltip={INPUT_FILTERS.keyword.tooltip} placeholder={INPUT_FILTERS.keyword.placeholder} inputClassName={FILTER_CONTROL_TEXT_CLASS_NAME} value={localKeyword} onChange={handleChange} />;
    $[6] = handleChange;
    $[7] = localKeyword;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
const MobileFiltersMenu = memo(function MobileFiltersMenu(t0) {
  const $ = _c(25);
  const {
    filters,
    setFilters,
    selectedCoinPair,
    setSelectedCoinPair
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(filters);
  const [draftSelectedCoinPair, setDraftSelectedCoinPair] = useState(selectedCoinPair);
  const wasOpenRef = useRef(false);
  const {
    volTvlEnabled
  } = useDebugSettings();
  let t1;
  if ($[0] !== volTvlEnabled) {
    t1 = volTvlEnabled ? [] : [SELECT_FILTERS.volTvl.label];
    $[0] = volTvlEnabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const volTvlFilterExclude = t1;
  let t2;
  if ($[2] !== filters || $[3] !== selectedCoinPair || $[4] !== volTvlEnabled) {
    t2 = getActiveFilterCount(displayFilters(filters, volTvlEnabled), selectedCoinPair);
    $[2] = filters;
    $[3] = selectedCoinPair;
    $[4] = volTvlEnabled;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const activeFilterCount = t2;
  let t3;
  let t4;
  if ($[6] !== filters || $[7] !== isOpen || $[8] !== selectedCoinPair) {
    t3 = () => {
      if (isOpen && !wasOpenRef.current) {
        setDraftFilters(filters);
        setDraftSelectedCoinPair(selectedCoinPair);
      }
      wasOpenRef.current = isOpen;
    };
    t4 = [filters, isOpen, selectedCoinPair];
    $[6] = filters;
    $[7] = isOpen;
    $[8] = selectedCoinPair;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="font-semibold text-text-primary">Filters</span>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Settings02 className="size-4" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== activeFilterCount) {
    t7 = <AriaButton className="flex h-11 w-full items-center justify-between rounded-lg bg-primary px-3 py-2 text-left shadow-xs ring-1 ring-primary outline-focus-ring ring-inset focus-visible:outline-2 focus-visible:outline-offset-2">{t5}<span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-sm font-semibold text-text-secondary shadow-xs">{t6}{activeFilterCount}</span></AriaButton>;
    $[13] = activeFilterCount;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== draftFilters || $[16] !== draftSelectedCoinPair || $[17] !== setFilters || $[18] !== setSelectedCoinPair || $[19] !== volTvlFilterExclude) {
    t8 = <AriaModalOverlay isDismissable={true} className={_temp2}>{t9 => {
        const {
          state
        } = t9;
        return <AriaModal className="w-full cursor-auto sm:max-w-2xl"><AriaDialog aria-label="Pool filters" className="outline-hidden focus:outline-hidden"><div className="flex max-h-[92dvh] min-h-0 w-full flex-col overflow-hidden rounded-t-2xl bg-primary shadow-xl sm:max-h-[90dvh] sm:rounded-lg"><div className="flex items-center justify-between border-b border-border-primary px-4 py-4"><h2 className="text-xl font-semibold text-text-primary">Filters</h2><button type="button" aria-label="Close filters" onClick={() => state.close()} className="rounded-full bg-secondary p-2 text-fg-quaternary outline-focus-ring hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2"><XClose className="size-5" /></button></div><div className="scrollbar-hide min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-4 py-5"><section><h3 className="mb-3 text-md font-semibold text-text-primary">Pair Type</h3><div className="grid grid-cols-2 gap-3"><PoolsPairs pairs={COIN_PAIRS} selectedCoinPair={draftSelectedCoinPair} onSelectPair={setDraftSelectedCoinPair} /></div></section><div className="mt-5 border-t border-border-primary pt-5"><div className="grid gap-4"><MobileDropdownFilters filters={draftFilters} setFilters={setDraftFilters} exclude={volTvlFilterExclude} /><KeywordFilter filters={draftFilters} setFilters={setDraftFilters} /></div></div></div><div className="shrink-0 border-t border-border-primary bg-primary p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"><div className="flex gap-3"><Button color="secondary" size="lg" className="shrink-0" onClick={() => {
                    setDraftFilters(defaultFilters);
                    setDraftSelectedCoinPair(null);
                  }}>Reset</Button><Button color="primary" size="lg" className="flex-1 justify-center" onClick={() => {
                    setFilters(draftFilters, {
                      immediate: true
                    });
                    setSelectedCoinPair(draftSelectedCoinPair);
                    state.close();
                  }}>Apply Filters</Button></div></div></div></AriaDialog></AriaModal>;
      }}</AriaModalOverlay>;
    $[15] = draftFilters;
    $[16] = draftSelectedCoinPair;
    $[17] = setFilters;
    $[18] = setSelectedCoinPair;
    $[19] = volTvlFilterExclude;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== isOpen || $[22] !== t7 || $[23] !== t8) {
    t9 = <div className="mb-6 lg:hidden"><AriaDialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>{t7}{t8}</AriaDialogTrigger></div>;
    $[21] = isOpen;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
});
MobileFiltersMenu.displayName = "MobileFiltersMenu";
const PoolsFiltersComponent = memo(function PoolsFiltersComponent() {
  const $ = _c(33);
  const {
    filters,
    selectedCoinPair,
    setFilters,
    resetFilters,
    setSelectedCoinPair
  } = usePoolsFilters();
  const {
    volTvlEnabled
  } = useDebugSettings();
  let t0;
  if ($[0] !== volTvlEnabled) {
    t0 = volTvlEnabled ? [] : [SELECT_FILTERS.volTvl.label];
    $[0] = volTvlEnabled;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const volTvlFilterExclude = t0;
  let t1;
  if ($[2] !== filters || $[3] !== selectedCoinPair || $[4] !== volTvlEnabled) {
    t1 = getActiveFilterCount(displayFilters(filters, volTvlEnabled), selectedCoinPair);
    $[2] = filters;
    $[3] = selectedCoinPair;
    $[4] = volTvlEnabled;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const hasAnyActiveFilters = t1 > 0;
  let t2;
  if ($[6] !== filters || $[7] !== selectedCoinPair || $[8] !== setFilters || $[9] !== setSelectedCoinPair) {
    t2 = <MobileFiltersMenu filters={filters} setFilters={setFilters} selectedCoinPair={selectedCoinPair} setSelectedCoinPair={setSelectedCoinPair} />;
    $[6] = filters;
    $[7] = selectedCoinPair;
    $[8] = setFilters;
    $[9] = setSelectedCoinPair;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h2 className="font-semibold text-text-primary">Filters</h2>;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== hasAnyActiveFilters || $[13] !== resetFilters) {
    t4 = hasAnyActiveFilters && <Button color="secondary" size="sm" iconLeading={X} onClick={resetFilters}>Reset Filters</Button>;
    $[12] = hasAnyActiveFilters;
    $[13] = resetFilters;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t4) {
    t5 = <div className="mb-4 flex h-5 items-center justify-start gap-4">{t3}{t4}</div>;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== filters || $[18] !== setFilters) {
    t6 = <div className="md:col-start-2 md:row-start-1 lg:col-start-4 lg:row-start-1"><KeywordFilter filters={filters} setFilters={setFilters} /></div>;
    $[17] = filters;
    $[18] = setFilters;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== filters || $[21] !== setFilters || $[22] !== volTvlFilterExclude) {
    t7 = <DropdownFilters filters={filters} setFilters={setFilters} exclude={volTvlFilterExclude} />;
    $[20] = filters;
    $[21] = setFilters;
    $[22] = volTvlFilterExclude;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t6 || $[25] !== t7) {
    t8 = <div className="grid w-full gap-3 md:grid-cols-2 lg:grid-cols-4">{t6}{t7}</div>;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t5 || $[28] !== t8) {
    t9 = <div className="hidden lg:block">{t5}{t8}</div>;
    $[27] = t5;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t2 || $[31] !== t9) {
    t10 = <div className="mb-6">{t2}{t9}</div>;
    $[30] = t2;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
});
PoolsFiltersComponent.displayName = "PoolsFilters";
const PoolsFilters = PoolsFiltersComponent as typeof PoolsFiltersComponent & {
  Select: typeof DropdownFilters;
  Keyword: typeof KeywordFilter;
};
PoolsFilters.Select = DropdownFilters;
PoolsFilters.Keyword = KeywordFilter;
export default PoolsFilters;
function _temp(item) {
  return <option key={item.id} value={item.id}>{item.label}</option>;
}
function _temp2(t0) {
  const {
    isEntering,
    isExiting
  } = t0;
  return cx("fixed inset-0 z-50 flex items-end overflow-hidden bg-overlay/70 backdrop-blur-[6px] sm:items-center sm:justify-center sm:overflow-y-auto sm:p-8", isEntering && "duration-300 ease-out animate-in fade-in", isExiting && "duration-200 ease-out animate-out fade-out");
}